// import type {LoaderData} from "~/root/loader.server";
// import type {CompanyMetadata} from "~/domainModels/companies";
// import type {CompanyWithMemberCount, IntellsysUser} from "~/utilities/typeDefinitions";
// import type {ReportPage} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/o/$companySlug/_reportScaffold/report/edit/$reportSlug/-state";
// import type {OsirisUser} from "@vesta/osiris-compatibility-module/utilities/typeDefinitions";
// import type {CanvasControls} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/o/$companySlug/_reportScaffold/report/edit/$reportSlug/-types";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {userValidator as userZodSchema} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {userDetailsValidator, type UserDetails} from "@vesta/vos-types/modules/platformLevel/userDetails/domain/UserDetails";
import {z as zod} from "zod";

// TODO: Shift to utilities

// export type RootLoaderData = LoaderData;

export enum TimeGranularity {
    daily = "Daily",
    weekly = "Weekly",
    monthly = "Monthly",
    // quarterly = "Quarterly",
    hourly = "Hourly",
    yearly = "Yearly",
}

export type EnumDictionary<T extends string | symbol | number, U> = {
    [t in T]: U;
};

export enum Environments {
    local = "local",
    dev = "dev",
    staging = "staging",
    prod = "prod",
}

export type Notification = {
    id: Uuid;
    title: string;
    description: string | null;
    userId: Uuid;
    isRead: boolean;
    createdAt: Iso8601DateTime;
};

export enum BackendFilterValueTypes {
    dataDriven = "d58a6ec2-e55c-4551-bb73-223e9b6f0a87",
    globalFilter = "63bba872-0783-47c0-8df9-366f3e78e962",
}

export enum BackendFilterTypes {
    atomic = "4b02c64c-de3c-43ae-a6be-b84baea167cb",
    or = "a35b6797-6a09-4b61-bf7b-0ff1da3a01df",
    and = "21049b0c-569f-4a37-9dc5-5fac882fff08",
}

export enum BackendFilterOperations {
    equals = "bafb6b6c-90ca-42bc-9ee9-2ac60ff6c3e7",
    in = "4e61f5af-3ed9-4ae9-befd-d8fdcb73ebad",
    contains = "0e55fe86-1062-4fe2-95be-92b9ae0c71ea",
    between = "1b40b607-8da0-48b0-9ab0-7fe53cab4a1d",
    greaterThan = "cdadd5e6-68f6-48e2-b56f-fc41ee91d2bb",
    lessThan = "f5aec8bc-42c4-4062-b82e-cd567d2e0091",
    greaterThanEqualTo = "15f683d5-f335-4ff6-8d4f-5cbe55e675bb",
    lessThanEqualTo = "f990d2dd-a7e5-46b7-887a-1c907c565e05",
}

export enum AtomicFilterValueType {
    canvasControl = "ac0457ec-e215-43e1-a969-7bb6bdc7a8af",
    dataDriven = "ceb59323-db41-43f2-abfe-5bb2925c8279",
}

export type AtomicFilter = {
    id: Uuid;
    filterType: BackendFilterTypes.atomic;
    attribute: Uuid | null;
} & (
    | {
          valueType: BackendFilterValueTypes.globalFilter;
          globalFilterId: Uuid;
      }
    | {
          valueType: BackendFilterValueTypes.dataDriven;
      }
) &
    // | {
    //       operation: BackendFilterOperations.contains | null;
    //       value: string | null;
    //       tempValue: string | null;
    //   }
    (| {
              operation: BackendFilterOperations.equals | BackendFilterOperations.contains | null;
              value: string | number | null;
              tempValue: string | number | null;
          }
        | {
              operation: BackendFilterOperations.in | null;
              value: Array<string> | null;
              tempValue: string | null;
          }
        | {
              operation:
                  | BackendFilterOperations.greaterThan
                  | BackendFilterOperations.greaterThanEqualTo
                  | BackendFilterOperations.lessThan
                  | BackendFilterOperations.lessThanEqualTo
                  | null;
              value: number | null;
              tempValue: number | null;
          }
        | {
              operation: BackendFilterOperations.between;
              value: [number | null, number | null];
              tempValue: [number | null, number | null];
          }
    );

export type OrBackendFilter = {
    id: Uuid;
    filters: Array<BackendFilter>;
    filterType: BackendFilterTypes.or;
};

export type AndBackendFilter = {
    id: Uuid;
    filters: Array<BackendFilter>;
    filterType: BackendFilterTypes.and;
};

// export type BackendFilter = AtomicFilter | OrBackendFilter | AndBackendFilter;
export type BackendFilter = {
    id: Uuid;
    filterType: BackendFilterTypes.and;
    filters: Array<AtomicFilter>;
};

export const backendFilterOperationsMetadata: EnumDictionary<BackendFilterOperations, {humanReadableName: string; isNumeric: boolean | null}> = {
    [BackendFilterOperations.equals]: {
        humanReadableName: "Equals",
        isNumeric: null,
    },
    [BackendFilterOperations.in]: {
        humanReadableName: "In",
        isNumeric: false,
    },
    [BackendFilterOperations.contains]: {
        humanReadableName: "Contains",
        isNumeric: false,
    },
    [BackendFilterOperations.greaterThan]: {
        humanReadableName: "Greater than",
        isNumeric: true,
    },
    [BackendFilterOperations.between]: {
        humanReadableName: "Between",
        isNumeric: true,
    },
    [BackendFilterOperations.greaterThanEqualTo]: {
        humanReadableName: "Greater than or Equal to",
        isNumeric: true,
    },
    [BackendFilterOperations.lessThan]: {
        humanReadableName: "Less than",
        isNumeric: true,
    },
    [BackendFilterOperations.lessThanEqualTo]: {
        humanReadableName: "Less than or Equal to",
        isNumeric: true,
    },
};

export const atomicFilterValueTypes: EnumDictionary<AtomicFilterValueType, {humanReadableName: string}> = {
    [AtomicFilterValueType.dataDriven]: {
        humanReadableName: "Data Driven",
    },
    [AtomicFilterValueType.canvasControl]: {
        humanReadableName: "Global Filter",
    },
    /* [AtomicFilterValueType.static]: {
        humanReadableName: "Static",
    }, */
};

export type ReportEmailTemplate = {
    id: Uuid;
    reportId: Uuid;
    templateHtml: string;
    widgetIds: Array<Uuid>;
    humanReadableName: string;
};

export type Collection = {
    id: Uuid;
    name: string;
    companyId: Uuid;
    reportIds: Array<Uuid>;
    slug: string;
};

export enum SlugStatus {
    active = "a89821f1-8fc4-4d1b-b82f-246de43cec32",
    passive = "99ae5c9e-afd8-4951-9568-d072d90462bd",
}

export enum UserAccessLevel {
    viewer = "3b797d5d-d5bf-4694-8fa4-60559939c2ab",
    editor = "7c9d2685-bb9f-4f8e-a2bf-6230dc4adf30",
    admin = "528a9707-ec7d-44be-8394-43a81ef98bd5",
    owner = "71ea95b9-437b-41d0-b4af-04a7b07172a6",
}

export enum CompanyDomainPermissions {
    openToJoin = "2268ff88-5f5a-4e96-8bc4-6adb4ca307b3",
    blacklist = "a172b907-2645-4faf-aa72-e309119ba307",
    requestToJoin = "43a5932e-e5b6-410e-a8b2-b13f97b694f5",
}

export enum CompanyRequestStatus {
    pending = "585c58aa-01ee-48d6-8375-d59d88053438",
}

export enum CompanyInvitationStatus {
    pending = "8af6484a-95e5-4dae-b307-89a25da84fb0",
}

export const userAccessLevelDisplayMetadata: EnumDictionary<UserAccessLevel, {humanReadableName: string}> = {
    [UserAccessLevel.viewer]: {
        humanReadableName: "Viewer",
    },
    [UserAccessLevel.editor]: {
        humanReadableName: "Editor",
    },
    [UserAccessLevel.admin]: {
        humanReadableName: "Admin",
    },
    [UserAccessLevel.owner]: {
        humanReadableName: "Owner",
    },
};

export enum ReportsForAddingToCollectionFetchType {
    suggested = "b9dbe0b3-db17-4931-b794-6cca5e24737e",
    createdByMe = "59e3396c-a4d4-4d25-bd6e-80efdf79dedd",
    collections = "417eb914-ff59-4c49-820d-f9952a60ba4d",
    all = "8fb3644e-4f23-479d-9f13-572a6846512b",
}

export type CompanyResourceCounts = {
    dataSources: number;
    users: number;
    creators: number;
    viewers: number;
};

export type CompanyResourceInformation = {
    workspaceId: Uuid;
    limits: CompanyResourceCounts;
    currentCounts: CompanyResourceCounts;
};

// TODO: Revisit the type name
export type CombinedUser = {
    platformUser: UserDetails;
    // TODO: Should I rename this to WorkspaceUser ??
    user: User;
};

export const combinedUserValidator: zod.ZodType<CombinedUser> = zod.object({
    platformUser: userDetailsValidator,
    user: userZodSchema,
});
